import { getIsQuietHoursOverlappingWithSTO } from 'modules/campaigns/lib/utils';
import { validateNumberRange } from 'modules/workflowNodeEditors/utils';
import { AttributionMode, Campaign, RateLimit } from 'modules/workflows/types';
import {
  ALERTS_STO_QH_CONFLICT_MESSAGE,
  ALERTS_STO_CONFLICT_MESSAGE,
  ALERTS_QH_CONFLICT_MESSAGE,
} from 'modules/campaigns/lib/constants';
import {
  CONVERSIONS_ATTR_PERIOD_TIME_RANGE,
  EVENT_ATT_PERIOD_TIME_RANGE,
  RATE_LIMIT_RANGE_PER_MINUTE,
  STO_COMPLETION_TIME_RANGE,
} from '../BaseSendEditor.constants';
import { CampaignErrors } from '../BaseSendEditor.types';
import {
  getModeNameFromConversions,
  getPeriodFromConversions,
} from '../ConversionSection/MultipleConversionSelection/MultipleConversionSelection.utils';
import { isStartAndEndTimeTheSame } from './BaseSendEditor.utils';
import { SendNodeValidation, ValidationState } from './SendNodeValidation';
import {
  isCampaignAlertsEnabled,
  isCampaignQuietHoursEnabled,
  isCampaignSTOEnabled,
} from './helpers';

interface ValidationOptions {
  isMultiConversion?: boolean;
}

export const doesQHHaveSameStartAndEnd = (campaign: Partial<Campaign> = {}) => {
  const { customQuietHours } = campaign;
  return customQuietHours
    ? isStartAndEndTimeTheSame(customQuietHours.startTime, customQuietHours.endTime)
    : false;
};

export const validateQHStartAndEnd = (campaign: Partial<Campaign> = {}) => {
  const hasError = doesQHHaveSameStartAndEnd(campaign);
  return new SendNodeValidation(hasError, 'Quiet hours can’t start and end at the same time.');
};

export const validateSTORange = (campaign: Partial<Campaign> = {}) => {
  if (!isCampaignSTOEnabled(campaign)) return new SendNodeValidation(false);
  const hrs = campaign?.schedulerMsgParams?.windowHours;
  const { hasError, message } = validateNumberRange(hrs as number, STO_COMPLETION_TIME_RANGE);
  return new SendNodeValidation(hasError, message);
};

export const validateSTOQHOverlap = (campaign: Partial<Campaign> = {}) => {
  const stoEnabled = isCampaignSTOEnabled(campaign);
  const qh = campaign?.customQuietHours;
  if (!stoEnabled || !qh) return new SendNodeValidation(false);
  const hrs = campaign?.schedulerMsgParams?.windowHours as number;

  const hasWarning = getIsQuietHoursOverlappingWithSTO(qh, hrs);
  const message =
    'Your quiet hours window overlaps with your send time optimization window—some or all of your sends won’t be optimized.';
  return new SendNodeValidation(hasWarning, message, ValidationState.Warning);
};

export const validateSTOQHAlertsOverlap = (campaign: Partial<Campaign> = {}) => {
  const isAlertsEnabled = isCampaignAlertsEnabled(campaign);
  const isSTOEnabled = isCampaignSTOEnabled(campaign);
  const isQuietHoursEnabled = isCampaignQuietHoursEnabled(campaign);

  let message = '';

  if (isAlertsEnabled) {
    if (isSTOEnabled && isQuietHoursEnabled) message = ALERTS_STO_QH_CONFLICT_MESSAGE;
    else if (isSTOEnabled) message = ALERTS_STO_CONFLICT_MESSAGE;
    else if (isQuietHoursEnabled) message = ALERTS_QH_CONFLICT_MESSAGE;
  }

  const hasWarning = message !== '';

  return new SendNodeValidation(hasWarning, message, ValidationState.Warning);
};

export const validateRateLimitRange = (campaign: Partial<Campaign> = {}) => {
  const { useRateLimit, rateLimitPerMin } = (campaign?.customRateLimit as RateLimit) ?? {};
  if (useRateLimit !== true) return new SendNodeValidation(false);
  const { hasError, message } = validateNumberRange(
    rateLimitPerMin as number,
    RATE_LIMIT_RANGE_PER_MINUTE,
  );
  return new SendNodeValidation(hasError, message);
};

export const validateMultiAttributionPeriod = (period: number) => {
  const { hasError, message } = validateNumberRange(
    period as number,
    CONVERSIONS_ATTR_PERIOD_TIME_RANGE,
  );
  return new SendNodeValidation(hasError, message);
};

export const validateMultiAttributionPeriodCampaign = (
  campaign: Partial<Campaign> = {},
  { isMultiConversion = false }: ValidationOptions = {},
) => {
  if (!isMultiConversion) {
    return new SendNodeValidation(false);
  }

  const { conversions = [] } = campaign;
  const mode = getModeNameFromConversions(conversions);

  if (mode !== AttributionMode.AttributionPeriod) {
    return new SendNodeValidation(false);
  }

  const period = getPeriodFromConversions(conversions);
  return validateMultiAttributionPeriod(period);
};

export const validateSingleAttributionPeriod = (period: number) => {
  const { hasError, message } = validateNumberRange(period as number, EVENT_ATT_PERIOD_TIME_RANGE);
  return new SendNodeValidation(hasError, message);
};

export const validateSingleAttributionPeriodCampaign = (
  campaign: Partial<Campaign> = {},
  { isMultiConversion = false }: ValidationOptions = {},
) => {
  const { attributionPeriodHours, attributionMode } = campaign?.customConversion || {};
  if (isMultiConversion || attributionMode !== AttributionMode.AttributionPeriod) {
    return new SendNodeValidation(false);
  }
  return validateSingleAttributionPeriod(attributionPeriodHours as number);
};

export const getCampaignErrors = (
  campaign: Partial<Campaign> = {},
  opts: ValidationOptions = {},
): CampaignErrors => {
  return {
    QHSameStartAndEnd: validateQHStartAndEnd(campaign),
    STORange: validateSTORange(campaign),
    STOQHOverlap: validateSTOQHOverlap(campaign),
    STOQHAlertsOverlap: validateSTOQHAlertsOverlap(campaign),
    RateLimitRange: validateRateLimitRange(campaign),
    MultiAttributionPeriod: validateMultiAttributionPeriodCampaign(campaign, opts),
    SingleAttributionPeriod: validateSingleAttributionPeriodCampaign(campaign, opts),
  };
};

export const isSTOValid = (campaign: Partial<Campaign> = {}, { STORange }: CampaignErrors = {}) => {
  return !(STORange?.hasError && isCampaignSTOEnabled(campaign));
};

export const isRateLimitValid = (
  { customRateLimit }: Partial<Campaign> = {},
  { RateLimitRange }: CampaignErrors = {},
) => !(RateLimitRange?.hasError && customRateLimit?.useRateLimit === true);
